import { Contracting_Cancel, Contracting_List, Contracting_Note } from "data/endpoints";
import DataGridState from "features/shared/components/dataGrid/DataGridState";
import React, { useState } from "react";
import ContractingSimplifiedDTO from "features/contracting/dtos/ContractingSimplifiedDTO";
import ContractingSimplified from "features/contracting/components/ContractingSimplified";
import ContractingSimplifiedHeader from "features/contracting//components/ContractingSimplifiedHeader";
import { loadDataFilterConfig } from "features/shared/helpers/modules";
import AlertWithButton from "features/shared/alerts/components/AlertWithButton";
import RolesChecker from "features/customer/components/RolesChecker";
import { useTranslation } from "react-i18next";
import AddContractingButton from "features/contracting/components/AddContractingButton";
import { useContractingAdd } from "../hooks/useContractingAdd";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IRepository from "data/IRepository";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { trackPromise } from "react-promise-tracker";
import ActionResultDTO from "features/shared/dtos/ActionResultDTO";
import { isSuccessStatus } from "data/helpers/responseHelper";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { getForAdminParam } from "features/shared/helpers/queryParams";

const contractingsDataFilterJson = loadDataFilterConfig(
  "contractingsDataFilter"
);
const pageSize = 10;

type Props = {
  forAdminUse?: boolean;
};

const ContractingsSection = ({ forAdminUse = false }: Props) => {
  // translations
  const tLabels = useTranslation("contracting\\contracting").t;

  // store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  const repo = useInjection<IRepository<object>>(IocTypes.Repository);
  const [reloadGrid, setReloadGrid] = useState(0);

  const handleCancel = async (contractingId: string) => {
    const response = await trackPromise(
      repo.post<ActionResultDTO<string>>({
        url: Contracting_Cancel,
        data: {},
        routeParams: getRouteParams("contractingId", contractingId),
        params: getForAdminParam(true),
      })
    );

    if (isSuccessStatus(response)) {
      setReloadGrid((k) => k + 1);
    }
  };

  const handleNoteSave = async (contractingId: string, note: string) => {
    const response = await trackPromise(
      repo.post<ActionResultDTO<string>>({
        url: Contracting_Note,
        data: { note: note },
        routeParams: getRouteParams("contractingId", contractingId),
        params: getForAdminParam(true),
      })
    );

    if (isSuccessStatus(response)) {
      setReloadGrid((k) => k + 1);
    }
  };

  // add contracting hook
  const { onAddClick } = useContractingAdd("/contracting/add");

  // helpers
  const getQueryParams = (): URLSearchParams => {
    let urlParams = new URLSearchParams();

    if (forAdminUse) {
      urlParams.set("ForAdminUse", forAdminUse.toString());
    } else {
      urlParams.set("CustomerId", customer.customerId!);
    }

    return urlParams;
  };

  // sub-renders
  const renderItem = (contracting: ContractingSimplifiedDTO) => {
    return (
      <ContractingSimplified
        contracting={contracting}
        forAdminUse={forAdminUse}
        onCancel={forAdminUse ? handleCancel : undefined}
        onNoteSave={forAdminUse ? handleNoteSave : undefined}
      />
    );
  };

  const renderHeader = () => {
    return <ContractingSimplifiedHeader forAdminUse={forAdminUse} />;
  };

  const renderNoContentAlert = () => {
    return (
      <AlertWithButton
        alertInfo={tLabels("contractingsSection.contractingsNotFound")}
        renderButton={!forAdminUse ? renderAddContractingBtn : undefined}
      />
    );
  };

  const renderAddContractingBtn = (): JSX.Element => {
    return (
      <RolesChecker enabledRoles="Contributor">
        <AddContractingButton onAddClick={onAddClick} />
      </RolesChecker>
    );
  };

  // render
  return (
    <DataGridState
      url={Contracting_List}
      pageSize={pageSize}
      filterJson={forAdminUse ? contractingsDataFilterJson : undefined}
      renderItem={renderItem}
      renderHeader={renderHeader}
      noFilterQueryParams={getQueryParams()}
      renderNoContent={renderNoContentAlert}
      renderGridHeaderBtns={renderAddContractingBtn}
      externalDependencies={[reloadGrid]}
    />
  );
};

export default ContractingsSection;
